import React, { useState, useEffect, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

// A more visually appealing spinner for the current step
const LoadingSpinner = () => (
  <div className="relative w-5 h-5">
    <div className="absolute inset-0 rounded-full border-2 border-gray-300 opacity-30"></div>
    <div className="absolute inset-0 rounded-full border-t-2 border-yellow-500 animate-spin"></div>
  </div>
);

// This interface defines the props for our component
interface LoadingStepProps {
  /** The main title to display on the loading screen. */
  title?: string;
  /** An array of steps to display in the checklist. */
  steps?: { id: string; text: string }[];
  animation?: ReactNode;
  stepInterval?: number;
}

export const LoadingStep: React.FC<LoadingStepProps> = ({
  title = "Processing...", // Default title
  steps = [], // Default to an empty array
  animation,
  stepInterval = 1000,
}) => {
  const { t } = useTranslation("cvEditor");
  const [activeStepIndex, setActiveStepIndex] = useState(0);

  // This effect animates the checklist, moving from one step to the next.
  useEffect(() => {
    if (steps.length === 0) return;

    const timer = setInterval(() => {
      setActiveStepIndex((prevIndex) => {
        // Stop the animation once the last step is reached
        if (prevIndex >= steps.length - 1) {
          clearInterval(timer);
          return prevIndex;
        }
        return prevIndex + 1;
      });
    }, stepInterval);

    return () => clearInterval(timer);
  }, [steps]);

  // The progress bar will be based on the animated active step.
  const progressPercentage =
    steps.length > 0 ? (activeStepIndex / steps.length) * 100 : 100;

  return (
    <div className="p-6 flex flex-col items-center mt-10 text-center h-full">
      {/* Main Animation Placeholder */}

      <h2 className="text-2xl font-bold text-gray-800 mb-8">
        {title || t("loading.defaultTitle")}
      </h2>

      {animation && <div className="mb-2 animate-fade-in">{animation}</div>}
      {/* Progress Bar */}
      {steps.length > 0 && (
        <div className="w-full max-w-sm bg-gray-200 rounded-full h-2 mb-4 overflow-hidden">
          <div
            className="bg-gradient-to-r from-yellow-400 to-yellow-500 h-full transition-all duration-700 ease-out"
            style={{ width: `${progressPercentage}%` }}
          ></div>
        </div>
      )}

      {/* Steps Checklist */}
      <div className="w-full max-w-sm space-y-1">
        {steps.map((step, index) => {
          const isCompleted = index < activeStepIndex;
          const isCurrent = index === activeStepIndex;

          return (
            <div
              key={step.id}
              className={`flex items-center gap-3 p-3 rounded-lg transition-all duration-300 ${
                isCurrent ? "bg-yellow-50 shadow-md" : ""
              }`}
            >
              <div className="flex-shrink-0 w-6 h-6 flex items-center justify-center">
                {isCompleted ? (
                  <CheckCircleIcon className="text-yellow-500" />
                ) : isCurrent ? (
                  <LoadingSpinner />
                ) : (
                  <div className="w-5 h-5 border-2 border-gray-300 rounded-full"></div>
                )}
              </div>
              <p
                className={`text-sm text-left transition-colors duration-300 ${
                  isCompleted
                    ? "text-gray-400 line-through"
                    : isCurrent
                    ? "font-medium text-gray-900"
                    : "text-gray-500"
                }`}
              >
                {step.text}
              </p>
            </div>
          );
        })}
      </div>
    </div>
  );
};
